import { useImage } from "@/api/hooks";
import { RefObject, useState } from "react";
import { motion, Variants } from "framer-motion";
import { Text } from "@/components/Typography";
import { Wine, WP_Image } from "@/models";
import { formatPrice } from "@/utils/formatPrice";
import {
	useShoppingCartCount,
	useShoppingCartItems,
	useShoppingCartTotal,
	useStore,
} from "@/hooks/store";
import { IconButton } from "@/components/Button";
import { ConfirmationDialog } from "../Dialogs/ConfirmationDialog";
import { useRouter } from "next/router";

import s from "./AddToCartButton.module.scss";
import { LineItem } from "@/models/orders";
import getShipping from "@/utils/getShipping";

interface Props {
	wine: Wine;
	portalRef: RefObject<HTMLDivElement>;
}

const buttonVariants: Variants = {
	default: {
		bottom: 18,
	},
	withCart: {
		bottom: 88,
	},
};

export function AddToCartButton({ portalRef, wine }: Props) {
	const [numberOfBottles, setNumberOfBottles] = useState(1);
	const [dialogLabels, setDialogLabels] = useState({
		text: "",
		buttonAccept: "",
		buttonCancel: "",
	});
	const { add, items } = useStore();
	const count = useShoppingCartCount();
	const [image] = useImage(wine.featured_media);
	const router = useRouter();
	const cartItems = useShoppingCartItems();
	const total = useShoppingCartTotal();

	const handleAddToCart = () => {
		for (let i = 0; i < numberOfBottles; i++) {
			add(wine);
		}

		if (
			numberOfBottles + items.length === 3 ||
			numberOfBottles + items.length === 6
		) {
			setDialogLabels({
				text: `Je hebt nu ${
					numberOfBottles + items.length
				} flessen en kunt bestellen, maar er meer uitkiezen kan natuurlijk ook.`,
				buttonAccept: "Bestellen",
				buttonCancel: "Kies een nieuw drinkmoment",
			});
		} else if (numberOfBottles + items.length === 12) {
			setDialogLabels({
				text: "Je hebt nu 12 flessen, bestellen maar!",
				buttonAccept: "Bestellen",
				buttonCancel: "",
			});
		} else {
			setDialogLabels({
				text: "Kies snel je volgende fles. Bestel per 3, 6 of 12 flessen. Zo kun je in 1 doosje wijn verschillende wijnen ontdekken en zorgen we er ook voor dat we niet onnodig karton verspillen en alleen goed gevulde dozen versturen.",
				buttonAccept: "",
				buttonCancel: "Kies een nieuw drinkmoment",
			});
		}

		setShowInfoDialog(true);
	};

	const handleDecrement = () =>
		setNumberOfBottles(Math.max(numberOfBottles - 1, 1));
	const handleIncrement = () => setNumberOfBottles(numberOfBottles + 1);

	const canAddMore = () => {
		if (count === 0) {
			return numberOfBottles < 12;
		}

		return numberOfBottles < 12 - count;
	};

	const [showInfoDialog, setShowInfoDialog] = useState<boolean>(false);

	const onConfirmOrder = async () => {
		const lineItems: Partial<LineItem>[] = cartItems.map(
			({ item, quantity }) => ({
				product_id: item.id,
				quantity,
			})
		);

		const body = JSON.stringify({
			line_items: lineItems,
			shipping_lines: [getShipping(total)],
		});

		const response = await fetch("/api/order", {
			method: "POST",
			body,
		});

		const order = await response.json();

		router.push(`/checkout/${order.id}`);
	};

	return (
		<motion.div
			className={s.button}
			variants={buttonVariants}
			animate={count ? "withCart" : "default"}
			transition={{ type: "tween" }}
		>
			<div className={s.button__left}>
				<div className={s.button__img_wrapper}>
					<BottleImage image={image} />
				</div>
				<span className={s.button__price}>{formatPrice(wine.price)}</span>
			</div>
			{wine.stock_status === "outofstock" ? (
				<div className={s.button__outOfStock}>
					<Text as="span">Niet op voorraad</Text>
				</div>
			) : (
				<>
					<div className={s.button__inputs}>
						<IconButton
							rank="secondary"
							size="small"
							icon="minus"
							label="Verlaag aantal flessen met 1"
							onClick={handleDecrement}
							className={s.button__inputs__minus}
							disabled={numberOfBottles === 1}
						/>
						<span className={s.button__inputs__value}>{numberOfBottles}</span>
						<IconButton
							rank="secondary"
							size="small"
							icon="plus"
							label="Verhoog aantal flessen met 1"
							onClick={handleIncrement}
							className={s.button__inputs__plus}
							disabled={!canAddMore()}
						/>
					</div>
					<div className={s.button__spacer} />
					<button
						className={s.button__add}
						onClick={handleAddToCart}
						disabled={count === 12}
					>
						Voeg toe
					</button>
				</>
			)}

			<ConfirmationDialog
				open={showInfoDialog}
				onConfirm={() => {
					setShowInfoDialog(true);
					onConfirmOrder();
				}}
				onClose={() => setShowInfoDialog(false)}
				onCancel={() => {
					setShowInfoDialog(false);
					router.push("/vragenlijst");
				}}
				portal={portalRef}
				title="In the pocket!"
				text={dialogLabels.text}
				buttonAccept={dialogLabels.buttonAccept}
				buttonCancel={dialogLabels.buttonCancel}
				isAddToCartButton={true}
			/>
		</motion.div>
	);
}

function BottleImage({ image }: { image: WP_Image | undefined }) {
	if (image) {
		return (
			<img
				className={s.button__img}
				src={image.media_details.sizes["bottle_23x80"].source_url}
				height={image.media_details.sizes["bottle_23x80"].height || 80}
				width={image.media_details.sizes["bottle_23x80"].width || 23}
				srcSet={[
					`${image.media_details.sizes["bottle_23x80"].source_url} 1x`,
					`${image.media_details.sizes["bottle_46x160"].source_url} 2x`,
					`${image.media_details.sizes["bottle_69x240"].source_url} 3x`,
				].join(", ")}
				alt=""
			/>
		);
	}

	return (
		<img
			className={s.button__img}
			src="/bottle.svg"
			height={80}
			width={23}
			alt=""
			role="presentation"
		/>
	);
}
